import dayjs from "dayjs";
import type { RegisterOptions } from "react-hook-form";

type Translate = (key: string) => string;

export interface FieldRuleSource {
  required?: boolean;
  errorMessage?: string;
  rule?: RegExp | string;
  errorMessageValidation?: string;
  maxLength?: number;
  maxLengthMessage?: string;
  validate?: boolean;
  blockInvalidInput?: boolean;
}

export const requiredRule = (
  value: boolean | undefined,
  message?: string
): Pick<RegisterOptions, "required"> => ({
  required: { value: Boolean(value), message: message as string },
});

export const requiredRuleWithFallback = (
  field: FieldRuleSource,
  t: Translate
) => requiredRule(field.required, field.errorMessage || t("field_required"));

export const patternRule = (
  field: FieldRuleSource,
  t: Translate
): Pick<RegisterOptions, "pattern"> => ({
  pattern: {
    value: field.rule as RegExp,
    message:
      field.errorMessageValidation ||
      t("error_msg_invalid_characters_change_your_input"),
  },
});

export const maxLengthRule = (
  field: FieldRuleSource
): Pick<RegisterOptions, "maxLength"> => ({
  maxLength: {
    value: field.maxLength as number,
    message: field.maxLengthMessage || "",
  },
});

export const requiredSelectionRule = (
  required: boolean | undefined,
  message?: string
): Pick<RegisterOptions, "validate"> => ({
  validate: (value: unknown) => {
    if (!required) return true;
    if (value === undefined || value === null || value === "") return message; // not `required`: react-hook-form treats false as empty, but "no" is a valid choice
    if (Array.isArray(value) && value.length === 0) return message;
    return true;
  },
});

export const requiredBooleanRule = (
  field: FieldRuleSource,
  t: Translate
): Pick<RegisterOptions, "validate"> => ({
  validate: (value: unknown) =>
    !field.required ||
    typeof value === "boolean" ||
    field.errorMessage ||
    t("field_required"),
});

const NO_DATE_PLACEHOLDER = "01.01.1900";

export const dateNotTodayRule = (
  field: FieldRuleSource,
  t: Translate
): Pick<RegisterOptions, "validate"> => ({
  validate: {
    value: (value: unknown) => {
      if (!field.validate) return undefined;
      const pickedDay = dayjs(value as string).format("DD.MM.YYYY");
      if (pickedDay === dayjs().format("DD.MM.YYYY")) {
        return field.errorMessageValidation;
      }
      if (pickedDay === NO_DATE_PLACEHOLDER) return t("field_required");
      return undefined;
    },
  },
});

export const isInputBlockedByRule = (
  field: FieldRuleSource,
  value: unknown
): boolean => {
  if (
    !field.blockInvalidInput ||
    !field.rule ||
    typeof value !== "string" ||
    value === ""
  ) {
    return false;
  }
  const rule =
    field.rule instanceof RegExp ? field.rule : new RegExp(field.rule);
  return !rule.test(value);
};

export const jsonTextRule = (
  t: Translate
): Pick<RegisterOptions, "validate"> => ({
  validate: (value: unknown) => {
    if (typeof value === "object" && value !== null) return true;
    if (!value || typeof value !== "string" || value.trim() === "") return true;
    try {
      JSON.parse(value);
      return true;
    } catch {
      return t("invalid_json_error");
    }
  },
});
